import { Select, Trans, t } from '@lingui/macro';
import { useState } from "react";
import { SoribotTestCase } from "../../../models/SoribotTestCase";
import { Clipboard } from "@capacitor/clipboard";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { Select as ShadcnSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";

export const TestCaseExportModal: React.FC<{ testcase: SoribotTestCase }> = ({ testcase }) => {
  const [show, setShow] = useState(false);
  const [text, setText] = useState('');

  function onPresent() {
    setText(testcase.questions.map(q => q.question).join('\n\n'));
  }

  const handleOpenChange = (open: boolean) => {
    setShow(open);
    if (open) {
      onPresent();
    }
  };

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost"><Trans>Export</Trans></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Export Questions</Trans></DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={copyToClipboard}><Trans>Copy to clipboard</Trans></Button>
          </div>
          <Textarea
            value={text}
            readOnly
            className="flex-1 min-h-[400px] font-mono text-sm"
          />
        </div>
      </DialogContent>
    </Dialog>
  );

  async function copyToClipboard() {
    await Clipboard.write({ string: text });
    setShow(false);
  }
}


export const TestCaseImportModal: React.FC<{ onDone: (questions: string[]) => void }> = ({ onDone }) => {
  const [show, setShow] = useState(false);
  const [text, setText] = useState('');
  const [separator, setSeparator] = useState('N');
  const [questions, setQuestions] = useState<string[]>([]);

  function onPresent() {
    setText('');
    setQuestions([]);
  }

  const handleOpenChange = (open: boolean) => {
    setShow(open);
    if (open) {
      onPresent();
    }
  };

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost"><Trans>Import</Trans></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Import Questions</Trans></DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="flex items-center gap-4">
            <Label><Trans>Separator</Trans></Label>
            <ShadcnSelect value={separator} onValueChange={setSeparator}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t`Select separator`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='N'><Trans>Single new line</Trans></SelectItem>
                <SelectItem value='NN'><Trans>Double new line</Trans></SelectItem>
                <SelectItem value=';'><Trans>Semicolon</Trans></SelectItem>
                <SelectItem value='?'><Trans>Question mark</Trans></SelectItem>
                <SelectItem value='tab'><Trans>Tabs</Trans></SelectItem>
              </SelectContent>
            </ShadcnSelect>

            {questions.length == 0 && <Button disabled={!text} onClick={makeQuestions} ><Trans>Next</Trans></Button>}
            {questions.length > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button variant="ghost" onClick={startOver}><Trans>Start over</Trans></Button>
                <Button onClick={finish}><Trans>Finish</Trans></Button>
              </div>
            )}
          </div>

          {questions.length == 0 && (
            <Textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={t`Paste questions separated with new lines.`}
              className="flex-1 min-h-[400px]"
            />
          )}

          {questions.length > 0 && (
            <div className="border rounded-md p-4 space-y-2">
              {questions.map((q, i) => (
                <div key={i} className="flex gap-4 p-2 hover:bg-gray-50 rounded">
                  <span className="font-bold text-gray-500 w-8">{i + 1}</span>
                  <div className="flex-1">{q}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  async function makeQuestions() {
    let sep = separator.replace('N', '\n').replace('tab', '\t');
    setQuestions(text.split(sep).map(q => q.trim()).filter(q => q.length > 0));
  }

  async function startOver() {
    setQuestions([]);
  }

  async function finish() {
    onDone(questions);
    setShow(false);
  }
}
